/**
 * QZ Tray Printer Service
 * 
 * Provides direct printing to label printers (Zebra, DYMO, Rollo, etc.)
 * without showing a print dialog.
 * 
 * Requirements:
 * - QZ Tray must be installed on the user's computer: https://qz.io/download/
 * - First-time use requires user to trust the certificate
 */

import qz from 'qz-tray';
import api, { printAgentApi } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface PrinterInfo {
  name: string;
  isDefault?: boolean;
}

export interface PrinterSettings {
  labelPrinter: string | null;
  documentPrinter: string | null;
  autoPrint: boolean;
  usePrintAgent?: boolean;
  agentDeviceId?: string | null;
  agentPrinterName?: string | null;
}

// ============================================================================
// STATE
// ============================================================================

let isConnected = false;
let connectionPromise: Promise<void> | null = null;
let cachedCert: string | null = null;

// Default settings stored in localStorage
const SETTINGS_KEY = 'printer_settings';

// ============================================================================
// CONNECTION MANAGEMENT
// ============================================================================

/**
 * Check if QZ Tray is installed and connected
 */
export function isQzConnected(): boolean {
  return isConnected && qz.websocket.isActive();
}

/**
 * Connect to QZ Tray
 * Returns immediately if already connected
 */
export async function connectToQz(): Promise<void> {
  // Already connected
  if (qz.websocket.isActive()) {
    isConnected = true;
    return;
  }

  // Connection in progress
  if (connectionPromise) {
    return connectionPromise;
  }

  // Start new connection
  connectionPromise = (async () => {
    try {
      // Configure certificate + signature using backend endpoints.
      // QZ Tray expects callback-style resolve/reject handlers here.
      qz.security.setCertificatePromise((resolve: (cert: string) => void, reject: (err: any) => void) => {
        if (cachedCert) {
          resolve(cachedCert);
          return;
        }

        const certUrl = `${api.defaults.baseURL || ''}/api/print/cert`;
        api.get('/api/print/cert')
          .then(response => {
            cachedCert = response.data?.cert;
            if (!cachedCert) {
              console.warn('[Printer] /api/print/cert returned no cert. Response:', response.status, response.data);
              reject(new Error('Print certificate is not configured'));
              return;
            }
            console.log('[Printer] Loaded cert from', certUrl, 'length:', cachedCert.length);
            resolve(cachedCert);
          })
          .catch((err: any) => {
            const url = err?.config?.url || err?.config?.baseURL || 'unknown';
            console.error('[Printer] Failed to load print certificate:', err?.message || err, 'URL:', url, 'Status:', err?.response?.status);
            reject(err);
          });
      });

      qz.security.setSignatureAlgorithm('SHA512');
      qz.security.setSignaturePromise((toSign: string) => {
        console.log('[Printer] Signature requested, payload length:', toSign?.length);
        return function(resolve: (sig: string) => void, reject: (err: any) => void) {
          console.log('[Printer] Inner signature function called, resolve type:', typeof resolve, 'reject type:', typeof reject);
          api.post('/api/print/sign', { toSign })
            .then(response => {
              if (!response.data?.signature) {
                console.error('[Printer] Signature missing from response');
                reject(new Error('Signature missing from response'));
                return;
              }
              console.log('[Printer] Signature received, length:', response.data.signature.length);
              resolve(response.data.signature);
            })
            .catch(err => {
              console.error('[Printer] Failed to sign print payload:', err?.message || err);
              reject(err);
            });
        };
      });

      await qz.websocket.connect();
      isConnected = true;
      console.log('[Printer] Connected to QZ Tray');
    } catch (error: any) {
      isConnected = false;
      console.error('[Printer] Failed to connect to QZ Tray:', error.message);
      throw new Error(
        'Could not connect to QZ Tray. Please ensure QZ Tray is installed and running. ' +
        'Download from: https://qz.io/download/'
      );
    } finally {
      connectionPromise = null;
    }
  })();

  return connectionPromise;
}

/**
 * Disconnect from QZ Tray
 */
export async function disconnectFromQz(): Promise<void> {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
    isConnected = false;
    console.log('[Printer] Disconnected from QZ Tray');
  }
}

// ============================================================================
// PRINTER DISCOVERY
// ============================================================================

/**
 * Get list of available printers
 */
export async function getPrinters(): Promise<PrinterInfo[]> {
  await connectToQz();
  
  console.log('[Printer] Requesting printer list...');
  const printers = await qz.printers.find();
  console.log('[Printer] Printer list received:', printers?.length, 'printers');
  const defaultPrinter = await qz.printers.getDefault();
  
  return printers.map((name: string) => ({
    name,
    isDefault: name === defaultPrinter,
  }));
}

/**
 * Get the default printer
 */
export async function getDefaultPrinter(): Promise<string | null> {
  await connectToQz();
  return qz.printers.getDefault();
}

// ============================================================================
// SETTINGS MANAGEMENT
// ============================================================================

/**
 * Get saved printer settings
 */
export function getPrinterSettings(): PrinterSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      return {
        usePrintAgent: false,
        agentDeviceId: null,
        agentPrinterName: null,
        autoPrint: false,
        ...JSON.parse(saved),
      };
    }
  } catch (e) {
    console.error('[Printer] Error loading settings:', e);
  }
  
  return {
    labelPrinter: null,
    documentPrinter: null,
    autoPrint: false,
    usePrintAgent: false,
    agentDeviceId: null,
    agentPrinterName: null,
  };
}

/**
 * Save printer settings
 */
export function savePrinterSettings(settings: PrinterSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  console.log('[Printer] Settings saved:', settings);
}

// ============================================================================
// PRINTING
// ============================================================================

/**
 * Print a PDF label directly to the label printer
 * 
 * @param pdfBase64 - Base64 encoded PDF data
 * @param printerName - Target printer (uses saved label printer if not specified)
 */
export async function printPdfLabel(
  pdfBase64: string,
  printerName?: string
): Promise<void> {
  const settings = getPrinterSettings();
  if (settings.usePrintAgent && settings.agentDeviceId) {
    // Attempt agent first; fall back to QZ if it fails
    try {
      await printAgentApi.createJob({
        device_id: settings.agentDeviceId,
        label_data: pdfBase64,
        label_format: 'pdf',
        printer_name: printerName || settings.agentPrinterName || undefined,
      });
      console.log('[Printer] Queued PDF label via print agent');
      return;
    } catch (err: any) {
      console.warn('[Printer] Print agent failed, falling back to QZ:', err?.message || err);
    }
  }

  await connectToQz();
  
  const printer = printerName || getPrinterSettings().labelPrinter;
  
  if (!printer) {
    throw new Error('No label printer configured. Please set up a label printer in Settings.');
  }

  // Remove data URL prefix if present
  const base64Data = pdfBase64.replace(/^data:[^;]+;base64,/, '');

  const config = qz.configs.create(printer, {
    // PDF-specific options
    colorType: 'grayscale',
    copies: 1,
  });

  const data = [{
    type: 'pixel' as const,
    format: 'pdf' as const,
    flavor: 'base64' as const,
    data: base64Data,
  }];

  try {
    await qz.print(config, data);
    console.log(`[Printer] PDF label sent to ${printer}`);
  } catch (error: any) {
    console.error('[Printer] Print failed:', error);
    throw new Error(`Print failed: ${error.message}`);
  }
}

/**
 * Print a PNG/image label directly to the label printer
 * 
 * @param imageBase64 - Base64 encoded image data
 * @param printerName - Target printer (uses saved label printer if not specified)
 */
export async function printImageLabel(
  imageBase64: string,
  printerName?: string
): Promise<void> {
  const settings = getPrinterSettings();
  if (settings.usePrintAgent && settings.agentDeviceId) {
    try {
      await printAgentApi.createJob({
        device_id: settings.agentDeviceId,
        label_data: imageBase64,
        label_format: 'png',
        printer_name: printerName || settings.agentPrinterName || undefined,
      });
      console.log('[Printer] Queued image label via print agent');
      return;
    } catch (err: any) {
      console.warn('[Printer] Print agent failed, falling back to QZ:', err?.message || err);
    }
  }

  await connectToQz();
  
  const printer = printerName || getPrinterSettings().labelPrinter;
  
  if (!printer) {
    throw new Error('No label printer configured. Please set up a label printer in Settings.');
  }

  // Remove data URL prefix if present
  const base64Data = imageBase64.replace(/^data:[^;]+;base64,/, '');

  const config = qz.configs.create(printer, {
    colorType: 'grayscale',
    copies: 1,
  });

  // Detect format from base64 header
  let format: 'png' | 'jpeg' | 'gif' = 'png';
  if (base64Data.startsWith('/9j/')) {
    format = 'jpeg';
  } else if (base64Data.startsWith('R0lGOD')) {
    format = 'gif';
  }

  const data = [{
    type: 'pixel' as const,
    format,
    flavor: 'base64' as const,
    data: base64Data,
  }];

  try {
    await qz.print(config, data);
    console.log(`[Printer] Image label sent to ${printer}`);
  } catch (error: any) {
    console.error('[Printer] Print failed:', error);
    throw new Error(`Print failed: ${error.message}`);
  }
}

/**
 * Print ZPL (Zebra Programming Language) directly to a Zebra printer
 * 
 * @param zplData - ZPL commands as string
 * @param printerName - Target printer (uses saved label printer if not specified)
 */
export async function printZpl(
  zplData: string,
  printerName?: string
): Promise<void> {
  const settings = getPrinterSettings();
  if (settings.usePrintAgent && settings.agentDeviceId) {
    try {
      await printAgentApi.createJob({
        device_id: settings.agentDeviceId,
        label_data: btoa(zplData),
        label_format: 'zpl',
        printer_name: printerName || settings.agentPrinterName || undefined,
      });
      console.log('[Printer] Queued ZPL via print agent');
      return;
    } catch (err: any) {
      console.warn('[Printer] Print agent failed, falling back to QZ:', err?.message || err);
    }
  }

  await connectToQz();
  
  const printer = printerName || getPrinterSettings().labelPrinter;
  
  if (!printer) {
    throw new Error('No label printer configured. Please set up a label printer in Settings.');
  }

  const config = qz.configs.create(printer);

  const data = [{
    type: 'raw' as const,
    format: 'plain' as const,
    data: zplData,
  }];

  try {
    await qz.print(config, data);
    console.log(`[Printer] ZPL sent to ${printer}`);
  } catch (error: any) {
    console.error('[Printer] Print failed:', error);
    throw new Error(`Print failed: ${error.message}`);
  }
}

/**
 * Print a shipping label (auto-detects format)
 * 
 * @param labelData - Base64 encoded label data
 * @param format - Label format hint ('pdf', 'png', 'zpl')
 * @param printerName - Target printer (optional)
 */
export async function printLabel(
  labelData: string,
  format?: 'pdf' | 'png' | 'zpl' | string,
  printerName?: string
): Promise<void> {
  const settings = getPrinterSettings();

  // Prefer agent if enabled
  if (settings.usePrintAgent && settings.agentDeviceId) {
    try {
      await printAgentApi.createJob({
        device_id: settings.agentDeviceId,
        label_data: labelData,
        label_format: format || undefined,
        printer_name: printerName || settings.agentPrinterName || undefined,
      });
      console.log('[Printer] Queued label via print agent');
      return;
    } catch (err: any) {
      console.warn('[Printer] Print agent failed, falling back to QZ:', err?.message || err);
    }
  }

  // Remove data URL prefix if present
  const cleanData = labelData.replace(/^data:[^;]+;base64,/, '');
  
  // Auto-detect format if not provided
  let detectedFormat = format?.toLowerCase() || 'pdf';
  
  if (!format) {
    // Check for common base64 signatures
    if (cleanData.startsWith('JVBERi')) {
      detectedFormat = 'pdf';
    } else if (cleanData.startsWith('iVBORw')) {
      detectedFormat = 'png';
    } else if (cleanData.startsWith('/9j/')) {
      detectedFormat = 'jpeg';
    } else if (cleanData.startsWith('^XA') || labelData.includes('^XA')) {
      detectedFormat = 'zpl';
    }
  }

  console.log(`[Printer] Printing label (format: ${detectedFormat})`);

  switch (detectedFormat) {
    case 'zpl':
      // ZPL is raw text, not base64
      const zplText = cleanData.startsWith('^XA') 
        ? cleanData 
        : atob(cleanData);
      await printZpl(zplText, printerName);
      break;
    case 'png':
    case 'jpeg':
    case 'gif':
      await printImageLabel(cleanData, printerName);
      break;
    case 'pdf':
    default:
      await printPdfLabel(cleanData, printerName);
      break;
  }
}

// ============================================================================
// TEST PRINT
// ============================================================================

/**
 * Print a test label to verify printer setup
 */
export async function printTestLabel(printerName: string): Promise<void> {
  await connectToQz();

  const config = qz.configs.create(printerName, {
    colorType: 'grayscale',
    copies: 1,
  });

  // Simple test using HTML
  const data = [{
    type: 'pixel' as const,
    format: 'html' as const,
    flavor: 'plain' as const,
    data: `
      <html>
        <body style="width: 4in; height: 6in; padding: 0.5in; font-family: Arial, sans-serif;">
          <h1 style="font-size: 24px; margin-bottom: 10px;">✓ Test Print Successful</h1>
          <p style="font-size: 14px;">Printer: ${printerName}</p>
          <p style="font-size: 14px;">Time: ${new Date().toLocaleString()}</p>
          <p style="font-size: 12px; margin-top: 20px; color: #666;">
            This is a test print from CraftyShip.
            If you can read this, your label printer is configured correctly.
          </p>
        </body>
      </html>
    `,
  }];

  try {
    await qz.print(config, data);
    console.log(`[Printer] Test label sent to ${printerName}`);
  } catch (error: any) {
    console.error('[Printer] Test print failed:', error);
    throw new Error(`Test print failed: ${error.message}`);
  }
}

// Export for use
const printerService = {
  isQzConnected,
  connectToQz,
  disconnectFromQz,
  getPrinters,
  getDefaultPrinter,
  getPrinterSettings,
  savePrinterSettings,
  printLabel,
  printPdfLabel,
  printImageLabel,
  printZpl,
  printTestLabel,
};

export default printerService;
